import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, ClipboardList, Copy, Pencil, Trash2 } from 'lucide-react';
import api from '@/services/apiClient';
import PageHeader from '@/components/ui/PageHeader';
import Card from '@/components/ui/Card';
import Badge from '@/components/ui/Badge';
import Button from '@/components/ui/Button';
import Select from '@/components/ui/Select';
import Toolbar from '@/components/ui/Toolbar';
import Modal from '@/components/ui/Modal';
import Spinner from '@/components/ui/Spinner';
import EmptyState from '@/components/ui/EmptyState';
import WorkoutBuilder from './WorkoutBuilder';
import styles from './Workouts.module.css';

export default function TrainerWorkouts() {
  const { t } = useTranslation();
  const [students, setStudents] = useState([]);
  const [studentId, setStudentId] = useState('');
  const [workouts, setWorkouts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    api
      .get('/students')
      .then((res) => {
        const list = (res.data || res).students || res.data || [];
        setStudents(list);
        if (list[0]) setStudentId(String(list[0].id));
      })
      .catch(() => setStudents([]));
  }, []);

  const load = () => {
    if (!studentId) return;
    setLoading(true);
    api
      .get('/workouts', { studentId })
      .then((res) => setWorkouts((res.data || res).workouts || res.data || []))
      .catch(() => setWorkouts([]))
      .finally(() => setLoading(false));
  };

  useEffect(load, [studentId]);

  const remove = async (w) => {
    if (!confirm(t('workouts.confirmDeleteWorkout', { name: w.name }))) return;
    try {
      await api.delete(`/workouts/${w.id}`);
      load();
    } catch (e) {
      alert(e.message);
    }
  };

  const duplicate = async (w) => {
    try {
      await api.post(`/workouts/${w.id}/duplicate`, {});
      load();
    } catch (e) {
      alert(e.message);
    }
  };

  return (
    <>
      <PageHeader kicker="Movez · Personal" title={t('nav.workouts')}>
        <Button icon={Plus} disabled={!studentId} onClick={() => setCreating(true)}>{t('screens.newWorkout')}</Button>
      </PageHeader>

      <Toolbar>
        <Select
          label={t('workouts.student')}
          value={studentId}
          onChange={(e) => setStudentId(e.target.value)}
          placeholder={students.length ? t('workouts.selectStudentPlaceholder') : t('workouts.noStudents')}
          options={students.map((s) => ({ value: String(s.id), label: s.name }))}
          style={{ minWidth: 260 }}
        />
      </Toolbar>

      {!studentId ? (
        <EmptyState icon={ClipboardList} title={t('screens.selectStudent')} description={t('workouts.selectStudentHint')} />
      ) : loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-12)' }}><Spinner /></div>
      ) : workouts.length === 0 ? (
        <EmptyState icon={ClipboardList} title={t('workouts.emptyTitle')} description={t('workouts.emptyDescription')}
          action={<Button icon={Plus} onClick={() => setCreating(true)}>{t('screens.newWorkout')}</Button>} />
      ) : (
        <div className={styles.grid}>
          {workouts.map((w) => (
            <Card key={w.id} className={styles.card}>
              <div className={styles.head}>
                <div>
                  {w.label && <Badge variant="accent">{w.label}</Badge>}
                  <div className={styles.name}>{w.name}</div>
                </div>
                <div style={{ display: 'flex', gap: 4 }}>
                  <button className={styles.icon} onClick={() => setEditing(w)} title={t('common.edit')}><Pencil size={15} /></button>
                  <button className={styles.icon} onClick={() => duplicate(w)} title={t('workouts.duplicate')}><Copy size={15} /></button>
                  <button className={styles.icon} onClick={() => remove(w)} title={t('common.delete')}><Trash2 size={15} /></button>
                </div>
              </div>
              {w.focus && <div className={styles.focus}>{w.focus}</div>}
              <div className={styles.count}>{t('workouts.exerciseCount', { count: w.exercises?.length ?? w.exerciseCount ?? 0 })}</div>
            </Card>
          ))}
        </div>
      )}

      <Modal
        open={creating || !!editing}
        onClose={() => { setCreating(false); setEditing(null); }}
        kicker={editing ? t('common.edit') : t('workouts.newKicker')}
        title={editing?.name || t('workouts.workoutModalTitle')}
        size="xl"
      >
        {(creating || editing) && (
          <WorkoutBuilder
            studentId={studentId}
            workout={editing}
            onSaved={() => { setCreating(false); setEditing(null); load(); }}
            onCancel={() => { setCreating(false); setEditing(null); }}
          />
        )}
      </Modal>
    </>
  );
}
